import type { ReactNode } from "react";

import { Absent } from "./PhoneShell.tsx";

/**
 * The frame the eight commerce, money and account screens are drawn in, built once.
 *
 * `13 WP12 Commerce and Multi-stop.dc.html` and `14 WP13 Money and Account.dc.html` are **one shell
 * with two `SC` tables**: the same markup, the same `renderVals`, the same palette, the same badge
 * table, the same footer. Sixteen screens between them pass sixteen sets of values through it. So
 * this is one component, and a difference between two screens is a difference in the data rather
 * than in the markup — which is the same decision `PhoneShell` records for the booking flow, taken
 * again because the evidence for it is the same.
 *
 * The DOM below is the design's own, element for element and in its order:
 *
 *     phone market
 *       bar                   the left control, the title and sub, the right control
 *       tray                  the scrolling middle
 *         card                the state chip, the title, the sub, and sometimes a four-step bar
 *         …                   the screen's own middle, composed by the screen in the design's order
 *       footer                the 52dp buttons, absent entirely when the screen has none
 *
 * ## It is not `PhoneShell`
 *
 * `PhoneShell` draws `10 Rider App.dc.html`: a hero with a floating app bar over it, and a sheet
 * that rises over a map. These two files draw a solid app bar, a tinted scrolling tray and a pinned
 * footer, with no hero anywhere. Three of the four rows differ and so does every value in them —
 * down to the app bar's control, which is a 38dp circle with a tray-coloured fill here and a 38dp
 * circle with a drop shadow there. Reusing one for the other would have meant a prop for every
 * difference, which is a component that draws neither design.
 *
 * ## The status bar is not here
 *
 * Same reason, said once in `PhoneShell` and true again: the signal strength and the battery level
 * are not published to a document, and a clock alone in a band whose other half was invented is
 * worse than a band this application does not claim. The acceptance check hides that row on the
 * design side and shortens the design's own screen by its measured height, so both sides are the
 * same geometry rather than nearly it.
 */

/** The app bar's left control. `menu` on `Merchants`, `arrow_back` on every other screen. */
export interface BarControl {
  readonly glyph: string;
  readonly label: string;
  readonly onPress?: () => void;
  /** Why pressing it cannot work. The control is still drawn, because the design draws it. */
  readonly unavailable?: string;
}

/** The badge kinds `K` publishes, each with its own shape as well as its own colour. */
export type BadgeKind = "idle" | "done" | "caution" | "unknown" | "closed" | "active" | "human";

export interface BadgeValue {
  readonly kind: BadgeKind;
  readonly label: string;
}

/** One 52dp action in the footer. The design's `btns`, where `primary` is its `p` flag. */
export interface MarketAction {
  readonly label: string;
  readonly glyph: string;
  readonly primary: boolean;
  readonly onPress?: () => void;
  /**
   * Why this action cannot be carried out, when the platform publishes no route for it.
   *
   * The button is still drawn, because the design draws it and nothing may be removed. Pressing it
   * says this instead of appearing to work. Five of the designed buttons on these eight screens are
   * in this state, and `navigation.ts` records which.
   */
  readonly unavailable?: string;
}

export interface MarketShellProps {
  /** The screen's own class, for the address and for a per-screen rule if one is ever needed. */
  readonly screen: string;
  readonly left: BarControl;
  readonly barTitle: string;
  /** A node for the same reason `sub` is: on three screens it is a fact nothing publishes. */
  readonly barSub: ReactNode;
  readonly right: BarControl;
  /** `d.right === 'sos'`: a red pill carrying the word rather than a circle carrying a glyph. */
  readonly rightIsSos?: boolean;
  readonly badge: BadgeValue;
  readonly title: string;
  /**
   * Drawn in place of `title` when the card's headline has no source.
   *
   * One screen reaches this and it is the screen the headline is the whole of:
   * `Multi-stop in trip` draws `Heading to Upper Hill`, which is a GEO-02 address read away from
   * the plan, and a refused or missing address must not become a plausible place name. `title` is
   * still required so that every call site states the words it would have drawn.
   */
  readonly titleAbsent?: string;
  /**
   * The card's second line.
   *
   * A node rather than a string, because on three of these screens half of it is published and half
   * of it is not — `Accepted 12:41 · ready about 12:55` is two facts, and COM-07 publishes the first
   * always and the second only once the store has said so. A string would have forced the whole line
   * to be either present or absent.
   */
  readonly sub: ReactNode;
  /** Said under the sub-line when the words on this screen are this build's, not the design's. */
  readonly flag?: string;
  readonly steps?: StepsValue;
  /** The screen's own middle, composed in the design's order by the screen itself. */
  readonly children?: ReactNode;
  readonly actions?: readonly MarketAction[];
}

export function MarketShell(props: MarketShellProps): ReactNode {
  const actions = props.actions ?? [];

  return (
    <div className={`phone market ${props.screen}`}>
      <div className="bar">
        <BarButton {...props.left} />

        <span className="bar__headings">
          <strong className="bar__title">{props.barTitle}</strong>
          <span className="bar__sub">{props.barSub}</span>
        </span>

        {props.rightIsSos === true ? (
          <button
            type="button"
            className="bar__sos"
            aria-label={props.right.label}
            {...unavailableProps(props.right.unavailable)}
            onClick={props.right.unavailable === undefined ? props.right.onPress : undefined}
          >
            SOS
          </button>
        ) : (
          <BarButton {...props.right} />
        )}
      </div>

      <div className="tray">
        <div className="card">
          <Badge value={props.badge} />
          <div className="card__headings">
            <strong className="card__title">
              {props.titleAbsent === undefined ? props.title : <Absent words={props.titleAbsent} />}
            </strong>
            <span className="card__sub">{props.sub}</span>
            {props.flag === undefined ? null : <span className="card__flag">{props.flag}</span>}
          </div>
          {props.steps === undefined ? null : <Steps {...props.steps} />}
        </div>

        {props.children}
      </div>

      {/* `display: btns.length ? 'grid' : 'none'` — the element is absent, not empty. */}
      {actions.length === 0 ? null : (
        <div className="footer">
          {actions.map((action) => (
            <button
              key={action.label}
              type="button"
              className={`action${action.primary ? " action--primary" : ""}`}
              {...unavailableProps(action.unavailable)}
              onClick={action.unavailable === undefined ? action.onPress : undefined}
            >
              <span className="glyph" aria-hidden="true">
                {action.glyph}
              </span>
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * `aria-disabled` rather than `disabled`, which is the choice `PhoneShell` makes and the reason it
 * gives: a control a screen reader cannot reach is worse than one that explains itself, and a
 * `disabled` button also changes its own rendering, which would move the comparison.
 */
function unavailableProps(
  because: string | undefined,
): { "aria-disabled": true; title: string } | Record<string, never> {
  return because === undefined ? {} : { "aria-disabled": true, title: because };
}

function BarButton(control: BarControl): ReactNode {
  return (
    <button
      type="button"
      className="bar__icon"
      aria-label={control.label}
      {...unavailableProps(control.unavailable)}
      onClick={control.unavailable === undefined ? control.onPress : undefined}
    >
      <span className="glyph" aria-hidden="true">
        {control.glyph}
      </span>
    </button>
  );
}

/**
 * The badge: a shape and a word, and never a colour alone.
 *
 * `unknown` is the only kind whose glyph carries text — a literal `?`, as the design's own `q` does —
 * and it is the kind this application reaches for whenever a service answered "nobody asked". The
 * other six shapes are built from borders and backgrounds in `market.css`, which is how the design
 * builds them too.
 */
export function Badge({
  value,
  small,
}: {
  readonly value: BadgeValue;
  readonly small?: boolean;
}): ReactNode {
  return (
    <span className={`badge badge--${value.kind}${small === true ? " badge--small" : ""}`}>
      <span className="badge__glyph" aria-hidden="true">
        {value.kind === "unknown" ? "?" : ""}
      </span>
      {value.label}
    </span>
  );
}

export interface StepsValue {
  /** The four labels, in order. The design hard-codes one set; see `OrderTracking.tsx`. */
  readonly labels: readonly [string, string, string, string];
  /** Which step is current. Earlier ones are filled, later ones are the hairline colour. */
  readonly current: number;
}

function Steps({ labels, current }: StepsValue): ReactNode {
  return (
    <div className="steps">
      {labels.map((label, index) => (
        <div key={label} className="steps__step">
          <span
            className={`steps__bar${
              index < current
                ? " steps__bar--done"
                : index === current
                  ? " steps__bar--current"
                  : ""
            }`}
          />
          <span className={`steps__label${index <= current ? " steps__label--reached" : ""}`}>
            {label}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------- the tray's middle, in the design's order */

/** One list row. `title` is the only part every screen fills. */
export interface RowValue {
  /** React only. Not `key`: this object is spread into `<Row>`, and `key` cannot be both. */
  readonly id: string;
  readonly lead: string;
  /** `li === 'check_circle'` is the one glyph the design colours green rather than brand. */
  readonly leadIsDone?: boolean;
  readonly title: string;
  /**
   * Drawn in place of `title` when the row's own name has no source.
   *
   * Every row of `Multi-stop in trip` is named by a GEO-02 address, and one address of several
   * failing to resolve is a row this build must not name — not a screen it must not draw.
   */
  readonly titleAbsent?: string;
  /** The row's second line, or the reason there is none. */
  readonly sub?: string;
  readonly subAbsent?: string;
  readonly badge?: BadgeValue;
  /** The right-hand figure, which is a price, a rating or an amount depending on the screen. */
  readonly amount?: string;
  readonly amountAbsent?: string;
  readonly control?: ReactNode;
  /** `opacity: .75` — a row that cannot be acted on, dimmed and still readable. */
  readonly dimmed?: boolean;
}

/**
 * The design's `groups`: a heading and a card of rows with hairlines between them.
 *
 * `heading` is `null` where the design writes `''`, which four of the eight screens do — the element
 * is then absent rather than an empty 12px line, because an empty line still takes the group's 6px
 * gap. `headingAbsent` is the third case and is the one this build reaches for most: a heading whose
 * own source is unpublished, drawn as the absence rather than dropped.
 */
export function Group({
  heading,
  headingAbsent,
  rows,
  empty,
}: {
  readonly heading?: string;
  readonly headingAbsent?: string;
  readonly rows: readonly RowValue[];
  /** What the card says when there is nothing in it. */
  readonly empty?: ReactNode;
}): ReactNode {
  return (
    <div className="group">
      {heading === undefined && headingAbsent === undefined ? null : (
        <span className="group__heading">
          {headingAbsent === undefined ? heading : <Absent words={headingAbsent} />}
        </span>
      )}
      <div className="group__list">
        {rows.length === 0 ? empty : rows.map((row) => <Row key={row.id} {...row} />)}
      </div>
    </div>
  );
}

function Row(row: RowValue): ReactNode {
  return (
    <div className={`row${row.dimmed === true ? " row--closed" : ""}`}>
      <span
        className={`row__lead glyph${row.leadIsDone === true ? " row__lead--done" : ""}`}
        aria-hidden="true"
      >
        {row.lead}
      </span>

      <span className="row__middle">
        <span className="row__title">
          {row.titleAbsent === undefined ? row.title : <Absent words={row.titleAbsent} />}
        </span>
        {row.sub === undefined && row.subAbsent === undefined ? null : (
          <span className="row__sub">
            {row.subAbsent === undefined ? row.sub : <Absent words={row.subAbsent} />}
          </span>
        )}
        {row.badge === undefined ? null : <Badge value={row.badge} small />}
      </span>

      <span className="row__right">
        {row.amount === undefined && row.amountAbsent === undefined ? null : (
          <span className="row__amount">
            {row.amountAbsent === undefined ? row.amount : <Absent words={row.amountAbsent} />}
          </span>
        )}
        {row.control}
      </span>
    </div>
  );
}

/** A row that cannot be acted on, said inside the group's own card so the card is never empty. */
export function EmptyRow({ words }: { readonly words: string }): ReactNode {
  return (
    <div className="row">
      <span className="row__middle">
        <span className="row__sub">
          <Absent words={words} />
        </span>
      </span>
    </div>
  );
}

/** `●` or `○`: one of a set, chosen or not, where `●` takes the brand colour. */
export function MarkControl({
  on,
  label,
  onPress,
  unavailable,
}: {
  readonly on: boolean;
  readonly label: string;
  readonly onPress?: () => void;
  readonly unavailable?: string;
}): ReactNode {
  return (
    <button
      type="button"
      className={`ctl ctl--mark${on ? " ctl--on" : ""}`}
      aria-pressed={on}
      aria-label={label}
      {...unavailableProps(unavailable)}
      onClick={unavailable === undefined ? onPress : undefined}
    >
      {on ? "●" : "○"}
    </button>
  );
}

/** `›`: the control on every row of `Settings`, which the design draws as text and not a glyph. */
export function ChevronControl({
  label,
  onPress,
  unavailable,
}: {
  readonly label: string;
  readonly onPress?: () => void;
  readonly unavailable?: string;
}): ReactNode {
  return (
    <button
      type="button"
      className="ctl ctl--mark"
      aria-label={label}
      {...unavailableProps(unavailable)}
      onClick={unavailable === undefined ? onPress : undefined}
    >
      ›
    </button>
  );
}

/** A glyph control in a brand-filled square, which is `add` on `Products · stock`. */
export function AddControl({
  label,
  onPress,
  unavailable,
}: {
  readonly label: string;
  readonly onPress?: () => void;
  readonly unavailable?: string;
}): ReactNode {
  return (
    <button
      type="button"
      className="ctl ctl--add glyph"
      aria-label={label}
      {...unavailableProps(unavailable)}
      onClick={unavailable === undefined ? onPress : undefined}
    >
      add
    </button>
  );
}

/** A ringed pill, which the design reaches for when a control's label is longer than two glyphs. */
export function PillControl({
  label,
  onPress,
  unavailable,
}: {
  readonly label: string;
  readonly onPress?: () => void;
  readonly unavailable?: string;
}): ReactNode {
  return (
    <button
      type="button"
      className="ctl ctl--pill"
      {...unavailableProps(unavailable)}
      onClick={unavailable === undefined ? onPress : undefined}
    >
      {label}
    </button>
  );
}

/**
 * `– 6 +`: the quantity stepper the design writes as one string.
 *
 * Three elements rather than one, because two thirds of it are controls. Each press is a COM-04
 * write under the cart's `expectedVersion`, which is what makes `Cart conflict` a designed state
 * rather than a surprise.
 */
export function Stepper({
  quantity,
  onChange,
  unavailable,
}: {
  readonly quantity: number;
  readonly onChange?: (next: number) => void;
  readonly unavailable?: string;
}): ReactNode {
  return (
    <span className="stepper">
      <button
        type="button"
        className="stepper__button"
        aria-label="One fewer"
        {...unavailableProps(unavailable)}
        onClick={unavailable === undefined ? (): void => onChange?.(quantity - 1) : undefined}
      >
        –
      </button>
      {quantity}
      <button
        type="button"
        className="stepper__button"
        aria-label="One more"
        {...unavailableProps(unavailable)}
        onClick={unavailable === undefined ? (): void => onChange?.(quantity + 1) : undefined}
      >
        +
      </button>
    </span>
  );
}

/**
 * The 409 card: `Your cart changed on another device`.
 *
 * `role="alert"` is the design's own attribute, not an addition — it writes `role="alert"` on this
 * element in the markup. The headline is the design's sentence word for word; the detail sentence
 * names the device and the time it happened, which `itemProvenanceAbsence` says no service
 * publishes, so that half is drawn as the absence.
 */
export function ConflictCard({
  headline,
  detail,
  detailAbsent,
}: {
  readonly headline: string;
  readonly detail?: string;
  readonly detailAbsent?: string;
}): ReactNode {
  return (
    <div className="conflict" role="alert">
      <span className="conflict__headline">
        <span className="conflict__mark" aria-hidden="true" />
        {headline}
      </span>
      <span className="conflict__detail">
        {detailAbsent === undefined ? detail : <Absent words={detailAbsent} />}
      </span>
    </div>
  );
}

/** One of the design's `rows`: a key on the left and a figure on the right, each in its own card. */
export interface TotalValue {
  /** The design's own key, and the React key. Named `label` so it cannot be `key`. */
  readonly label: string;
  readonly value?: string;
  readonly absent?: string;
}

/**
 * The totals, which the design styles by position rather than by meaning.
 *
 * `i === a.length - 1` is the heavy row, and that last row is 16px rather than 13.5 when its key
 * matches `/Total|New estimate/`. Copied as written: the design's rule is "the last figure of a set
 * is the one the screen is about", and on `Cart` that is `Total` while on `Order tracking` it is
 * `Courier`, which is correctly *not* enlarged.
 */
export function Totals({ rows }: { readonly rows: readonly TotalValue[] }): ReactNode {
  return (
    <>
      {rows.map((row, index) => {
        const last = index === rows.length - 1;
        const total = last && /Total|New estimate/.test(row.label);
        return (
          <div key={row.label} className="totals">
            <span className="totals__key">{row.label}</span>
            <span
              className={`totals__value${last ? " totals__value--last" : ""}${
                total ? " totals__value--total" : ""
              }`}
            >
              {row.absent === undefined ? row.value : <Absent words={row.absent} />}
            </span>
          </div>
        );
      })}
    </>
  );
}

/** The design's `note`, which is an `info` glyph and a sentence on a tinted card. */
export function InfoNote({ text }: { readonly text: string }): ReactNode {
  return (
    <div className="info">
      <span className="glyph" aria-hidden="true">
        info
      </span>
      <span>{text}</span>
    </div>
  );
}
